import React, { useEffect, useState, useCallback } from "react";
import { Dialog, Alert } from "../../../components";
import {
  createLocationOperation,
  getLocationParams,
} from "../../api/salesPurchases";
import { uiLanguage, userClientDb } from "../../auth/selectors/userSelector";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  getErrorMessage,
  getWarningMessage,
} from "../../../components/form/formUtils";
import { Navigate } from "react-router-dom";
import * as routes from "../../../app/routes";
import { Grid2 as Grid } from "@mui/material";
import { Form } from "react-final-form";
import {
  OutlinedSelectField,
  OutlinedTextField,
} from "../../../components/form";
import { required } from "../../../utils/validate";
import { useLoader } from "../../../hooks";

interface ILocationCreate {
  open: boolean;
  onClose: (event?: Record<string, unknown>) => void;
}

const subscription = {
  submitError: true,
  submitting: true,
  errors: true,
};

let id = "";

interface LocationOperation {
  parameter: string;
  code: string;
}

function LocationCreate(props: ILocationCreate) {
  const { open, onClose } = props;
  const [documentTypeOptions, setDocumentTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [redirect, setRedirect] = useState({ redirect: false, id: "" });
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    error: false,
  });
  const [translate] = useTranslation();
  const locale = useSelector(uiLanguage);
  const userDb = useSelector(userClientDb);
  const [, setLoader] = useLoader();

  const documentTypes = [
    { value: "1", label: translate("location_change", "Lokacijos keitimas") },
    { value: "2", label: translate("pallet_assembly", "Paletės surinkimas") },
    { value: "3", label: translate("pallet_disassembly", "Paletės ardymas") },
    { value: "5", label: translate("list", "Sąrašas") },
  ];

  const params = {
    clientDbId: userDb ? userDb.id : 0,
    locale: locale,
    module: "0E",
  };

  const initialValues = React.useMemo(
    () => ({
      documentType:
        documentTypeOptions && documentTypeOptions[0]
          ? documentTypeOptions[0].value
          : "",
      descriptionCode1: "",
    }),
    [documentTypeOptions],
  );

  useEffect(() => {
    setLoader(true);
    getLocationParams(params)
      .then(({ data }: { data: LocationOperation[] }) => {
        setLoader(false);
        if (data && data.length > 0) {
          const docTypes = data.filter(
            (operation) => operation.parameter.toLowerCase() === "doctypes",
          );

          if (docTypes.length > 0) {
            const filteredDocTypes = docTypes.map((docType) => {
              const documentType = documentTypes.find(
                (item) => item.value === String(docType.code),
              );
              return {
                value: String(docType.code),
                label: documentType ? documentType.label : docType.code,
              };
            });

            setDocumentTypeOptions(filteredDocTypes);
          }
        }
      })
      .catch((reason) => {
        setLoader(false);
        const message = getErrorMessage(reason);
        setShowAlert({ show: true, message: message.message, error: true });
      });
  }, []);

  const handleSubmit = (values: any) => {
    const opParams = {
      ...params,
      description: values.description,
      type: values.documentType,
    };

    createLocationOperation(opParams)
      .then(({ data, warnings }) => {
        if (warnings && warnings.length > 0) {
          id = warnings[0].operationCode;
          const message = getWarningMessage(warnings);
          setShowAlert({ show: true, message: message, error: false });
          return;
        }

        id = data[0].operationCode;
        setRedirect({ redirect: true, id: id });
      })
      .catch((reason) => {
        const message = getErrorMessage(reason);
        setShowAlert({ show: true, message: message.message, error: true });
      });
  };

  const handleWarningClose = useCallback(() => {
    setRedirect({ redirect: true, id: id });
  }, [setRedirect, id]);

  const handleErrorClose = () => {
    setShowAlert({ show: false, message: "", error: false });
    onClose();
  };

  const showError = (
    <Alert
      isOpen={showAlert.show}
      message={showAlert.message}
      onClose={showAlert.error ? handleErrorClose : handleWarningClose}
      iconColor={showAlert.error ? "error" : "warning"}
    />
  );

  return (
    <div>
      {showAlert.show && showError}
      {redirect.redirect ? (
        <Navigate to={routes.ROUTE_LOCATION_DETAILS.path + "/" + redirect.id} />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          subscription={subscription}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} method="post">
              <Dialog
                open={open}
                onClose={onClose}
                title={translate("createOperation", "Nauja operacija")}
                isConfirmationButton
                onConfirm={handleSubmit}
                confirmButtonLabel={translate("create")}
              >
                <Grid container spacing={1} direction="column">
                  <Grid size={12}>
                    <OutlinedSelectField
                      name="documentType"
                      fullWidth
                      label={translate("docType", "Dokumento tipas")}
                      options={documentTypeOptions}
                      validate={required}
                      required
                    />
                  </Grid>

                  <Grid size={12}>
                    <OutlinedTextField
                      name="description"
                      fullWidth
                      label={translate("remarks", "Pastabos")}
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>
              </Dialog>
            </form>
          )}
        />
      )}
    </div>
  );
}

export default LocationCreate;
